import { t } from "i18next";
import { parseRegExp } from "src/common/parseRegExp";

import { cmpScalarValues } from "../../../../../common/cmpScalarValues";
import { getScalarValue } from "../components/dataUtils.ts/getScalarValue";
import { getStringValue } from "../components/dataUtils.ts/getStringValue";
import { MdmCardFormStore } from "../MdmCardFormStore";
import { ZAttrValue } from "./dataTypes";
import { FormField } from "./MdmCardFormTypes";
import { ZRule } from "./rules";
import { Validator } from "./Validator";

type ErrorParams = {
  msg: string | null | undefined;
  defaultMsg: () => string;
  form: MdmCardFormStore;
  field: FormField;
};

const makeError = ({ msg, defaultMsg, form, field }: ErrorParams): Error => {
  const text: string = msg || defaultMsg();
  const errMsg = form.evaluateText(text, field.path);
  return new Error(errMsg);
};

const fail = (params: ErrorParams): Promise<void> =>
  Promise.reject(makeError(params));

const isEmpty = (aValue: ZAttrValue | null): boolean => {
  const scalar = getScalarValue(aValue);
  // Считаем, что 0 - это заполненное значение
  if (scalar === 0) return true;
  return !scalar;
};

/* eslint consistent-return: "off" */

export const createValidator = (
  rule: ZRule,
  field: FormField,
  form: MdmCardFormStore,
): Validator => {
  // eslint-disable-next-line default-case
  switch (rule.type) {
    case "required":
      return {
        test: () =>
          isEmpty(field.data)
            ? fail({
                msg: rule.msg,
                defaultMsg: () => t("Field required"),
                form,
                field,
              })
            : Promise.resolve(),
        isRequired: true,
      };
    case "pattern":
      return {
        test: () => {
          const dataNode = field.data;
          if (dataNode) {
            const text = getStringValue(dataNode);
            if (text) {
              try {
                const regExp = parseRegExp(rule.pattern);
                if (!regExp.test(text)) {
                  return fail({
                    msg: rule.msg,
                    defaultMsg: () => "Значение не соответствует шаблону",
                    form,
                    field,
                  });
                }
              } catch (e) {
                return Promise.reject(e);
              }
            }
          }
          return Promise.resolve();
        },
      };
    case "min":
      return {
        test: () => {
          const curValue = getScalarValue(field.data);
          if (curValue !== null) {
            if (curValue < rule.minValue) {
              return fail({
                msg: rule.msg,
                defaultMsg: () =>
                  t("Min value required", { value: rule.minValue }),
                form,
                field,
              });
            }
          }
          return Promise.resolve();
        },
      };
    case "cmpField":
      return {
        test() {
          const thisValue = getScalarValue(field.data);
          const otherValue = form.calcExprValue(rule.path, field);
          if (
            thisValue !== "" &&
            otherValue !== "" &&
            cmpScalarValues(rule.op, thisValue, otherValue) === false
          ) {
            return fail({
              msg: rule.msg,
              defaultMsg: () => `Должно быть ${rule.op} ${rule.path}`,
              form,
              field,
            });
          }
          return Promise.resolve();
        },
      };
  }
};
